import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageCircle, Send, Users, Search, ArrowLeft, 
  Plus, Lock, ChevronRight, User, Phone
} from 'lucide-react';
import { toast } from 'sonner';

// Color constants for visual distinction
const TEAM_CHAT_COLOR = 'bg-blue-600 hover:bg-blue-700';
const TEAM_CHAT_BG = 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
const EXTERNAL_TEXT_COLOR = 'bg-purple-600 hover:bg-purple-700';
const EXTERNAL_TEXT_BG = 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';

const MessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { api, user, isFeatureEnabled } = useAuth();
  
  // Determine initial tab from URL params
  const initialTab = searchParams.get('tab') === 'external' ? 'external' : 'team';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // View state: 'list', 'new-conversation', 'conversation'
  const [view, setView] = useState('list');
  
  // Data state
  const [internalUsers, setInternalUsers] = useState([]);
  const [externalContacts, setExternalContacts] = useState([]);
  const [teamConversations, setTeamConversations] = useState([]);
  const [externalConversations, setExternalConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  
  // New conversation state
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [externalPhoneNumber, setExternalPhoneNumber] = useState('');
  
  const messagesEndRef = useRef(null);

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';

  useEffect(() => {
    if (isOnboarded) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isOnboarded]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load internal users for team chat
      const usersRes = await api.get('/users/all');
      setInternalUsers(usersRes.data.filter(u => u.id !== user?.id));
      
      // Load external contacts
      const contactsRes = await api.get('/contacts');
      setExternalContacts(contactsRes.data.external || []);
      
      // Load conversations (mocked for now)
      await loadConversations();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');
      const allConvos = res.data || [];
      // Separate by type
      setTeamConversations(allConvos.filter(c => c.type === 'team'));
      setExternalConversations(allConvos.filter(c => c.type === 'external'));
      return allConvos;
    } catch (error) {
      // Mock empty for now
      setTeamConversations([]);
      setExternalConversations([]);
      return [];
    }
  };

  // Deep-link: open the external conversation for a specific contact
  // when arriving via a notification link like
  // `/messages?contact={contact_id}`. We resolve the contact to its
  // matching external conversation (or create it if none exists yet)
  // and switch to the conversation view automatically.
  useEffect(() => {
    const contactId = searchParams.get('contact');
    if (!contactId || loading) return;
    // Find an existing external conversation tied to this contact.
    const existing = externalConversations.find(
      (c) => c.contact_id === contactId
        || c.related_id === contactId
        || c.recipient_id === contactId
    );
    if (existing) {
      handleSelectConversation(existing);
      return;
    }
    // Otherwise switch to the External tab so the user sees the contact
    // in the list and can tap to start the thread.
    setActiveTab('external');
  }, [searchParams, externalConversations, loading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadMessages = async (conversationId) => {
    try {
      const res = await api.get(`/messages/conversation/${conversationId}`);
      setMessages(res.data || []);
    } catch (error) {
      setMessages([]);
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setView('conversation');
    await loadMessages(conv.id);
  };

  const handleStartNewConversation = () => {
    setToSearchQuery('');
    setSelectedRecipient(null);
    setExternalPhoneNumber('');
    setView('new-conversation');
  };

  const handleSelectRecipient = (recipient) => {
    setSelectedRecipient(recipient);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    // If we have a selected recipient but no conversation yet, create one
    if (selectedRecipient && !selectedConversation) {
      try {
        const res = await api.post('/messages/conversations', {
          type: activeTab === 'team' ? 'team' : 'external',
          recipient_id: selectedRecipient.id,
          recipient_type: activeTab === 'team' ? 'user' : 'external_contact'
        });
        setSelectedConversation(res.data);
        // Now send message
        await api.post('/messages/send', {
          conversation_id: res.data.id,
          content: newMessage.trim()
        });
        toast.success('Message sent');
        setNewMessage('');
        loadConversations();
      } catch (error) {
        toast.error('Failed to send message');
      }
      return;
    }

    if (!selectedConversation) return;

    try {
      await api.post('/messages/send', {
        conversation_id: selectedConversation.id,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        content: newMessage.trim(),
        sender_id: user?.id,
        timestamp: new Date().toISOString()
      }]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedConversation(null);
    setSelectedRecipient(null);
    setMessages([]);
  };

  // Helper functions
  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name} ${u.last_name}`;
    return u?.full_name || u?.name || 'Unknown';
  };

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    if (u?.name) return u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return 'U';
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  // Filtered lists for "To" search
  const filteredInternalUsers = internalUsers.filter(u =>
    getFullName(u).toLowerCase().includes(toSearchQuery.toLowerCase())
  );

  const filteredExternalContacts = externalContacts.filter(c => {
    const nameMatch = (c.name || '').toLowerCase().includes(toSearchQuery.toLowerCase());
    const phoneMatch = (c.phone || '').includes(toSearchQuery);
    return nameMatch || phoneMatch;
  });

  // Check if phone number format
  const isPhoneNumber = (str) => /^[\d\s\-\+\(\)]+$/.test(str) && str.replace(/\D/g, '').length >= 7;

  // Non-onboarded blocker
  if (!isOnboarded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="messages-page">
        <div className="max-w-2xl mx-auto pt-4 relative">
          <div className="absolute inset-0 backdrop-blur-md bg-white/50 dark:bg-black/50 rounded-xl z-10 flex flex-col items-center justify-center min-h-[400px]">
            <Lock className="w-8 h-8 text-amber-600 mb-4" />
            <h2 className="text-xl font-bold mb-2">Complete Onboarding</h2>
            <p className="text-muted-foreground text-center max-w-sm mb-4">
              Complete your onboarding to access messages.
            </p>
            <Button onClick={() => navigate('/')}>Return to Onboarding</Button>
          </div>
        </div>
      </div>
    );
  }

  // Conversation View
  if (view === 'conversation' && selectedConversation) {
    const isTeam = selectedConversation.type === 'team';
    return (
      <div className={`fixed inset-x-0 ${isTeam ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-purple-50/50 dark:bg-purple-950/20'}`} style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 0 }} data-testid="conversation-view">
        {/* Header (fixed at top of frame) */}
        <div className={`absolute top-0 left-0 right-0 flex items-center gap-3 m-3 mb-2 p-3 rounded-xl ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}>
          <Button variant="ghost" size="sm" onClick={handleBackToList}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Avatar className="h-10 w-10">
            {isTeam ? (
              <>
                <AvatarImage src={selectedConversation.other_user?.profile_picture} />
                <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(selectedConversation.other_user?.role)} text-white`}>
                  {getInitials(selectedConversation.other_user)}
                </AvatarFallback>
              </>
            ) : (
              <AvatarFallback className="bg-purple-500 text-white">
                <User className="w-5 h-5" />
              </AvatarFallback>
            )}
          </Avatar>
          <div className="flex-1">
            <p className="font-semibold">
              {isTeam ? getFullName(selectedConversation.other_user) : selectedConversation.external_contact?.name || selectedConversation.phone_number}
            </p>
            <p className={`text-xs ${isTeam ? 'text-blue-600' : 'text-purple-600'}`}>
              {isTeam ? 'Team Chat' : 'External Text'}
            </p>
          </div>
        </div>

        {/* Messages area - scrolls between header and input rail */}
        <div className="absolute inset-x-0 overflow-y-auto px-3" style={{ top: '5.25rem', bottom: 'calc(env(safe-area-inset-bottom, 0px) + max(8.5rem, var(--kb-offset, 0px) + 4.5rem))' }}>
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No messages yet</p>
                <p className="text-xs">Start the conversation</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl break-words whitespace-pre-wrap ${
                      msg.sender_id === user?.id
                        ? isTeam ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-[#1a1a1a] text-foreground'
                    }`}
                  >
                    <p className="text-sm break-words">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${msg.sender_id === user?.id ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input rail — fixed above bottom nav, lifts above keyboard via --kb-offset */}
        <div
          className={`fixed inset-x-0 z-40 flex gap-2 m-3 p-3 rounded-xl ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + max(4.25rem, var(--kb-offset, 0px) + 0.5rem))' }}
        >
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            className="flex-1"
            style={{ fontSize: '16px' }}
          />
          <Button onClick={handleSendMessage} className={isTeam ? TEAM_CHAT_COLOR : EXTERNAL_TEXT_COLOR}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // New Conversation View
  if (view === 'new-conversation') {
    const isTeam = activeTab === 'team';
    return (
      <div className={`fixed inset-x-0 flex flex-col p-3 kb-aware-bottom ${isTeam ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-purple-50/50 dark:bg-purple-950/20'}`} style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))' }} data-testid="new-conversation-view">
        {/* Header */}
        <div className={`flex-shrink-0 flex items-center gap-3 mb-3 p-3 rounded-xl ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}>
          <Button variant="ghost" size="sm" onClick={handleBackToList}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <p className="font-semibold">New Conversation</p>
            <p className={`text-xs ${isTeam ? 'text-blue-600' : 'text-purple-600'}`}>
              {isTeam ? 'Team Chat' : 'External Text'}
            </p>
          </div>
        </div>

        {/* To: Search Bar */}
        <div className={`flex-shrink-0 p-3 rounded-xl mb-3 ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isTeam ? 'text-blue-600' : 'text-purple-600'}`}>To:</span>
            {selectedRecipient ? (
              <div className="flex items-center gap-2 bg-white dark:bg-[#1a1a1a] rounded-full px-3 py-1">
                <span className="text-sm">{isTeam ? getFullName(selectedRecipient) : selectedRecipient.name || selectedRecipient.phone}</span>
                <button onClick={() => setSelectedRecipient(null)} className="text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="w-3 h-3 rotate-180" />
                </button>
              </div>
            ) : (
              <Input
                placeholder={isTeam ? "Search team members..." : "Search contacts or enter phone number..."}
                value={toSearchQuery}
                onChange={(e) => setToSearchQuery(e.target.value)}
                className="flex-1"
                style={{ fontSize: '16px' }}
                autoFocus
              />
            )}
          </div>
        </div>

        {/* Search Results — fills remaining space, scrolls */}
        {!selectedRecipient ? (
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-1">
              {isTeam ? (
                // Internal users only for Team Chat
                filteredInternalUsers.length > 0 ? (
                  filteredInternalUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 p-3 bg-white dark:bg-[#1a1a1a] rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      onClick={() => handleSelectRecipient(u)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={u.profile_picture} />
                        <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(u.role)} text-white`}>
                          {getInitials(u)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{getFullName(u)}</p>
                        <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8 text-sm">No team members found</p>
                )
              ) : (
                // External contacts only for External Texts
                <>
                  {filteredExternalContacts.length > 0 && (
                    filteredExternalContacts.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-3 p-3 bg-white dark:bg-[#1a1a1a] rounded-lg cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                        onClick={() => handleSelectRecipient(c)}
                      >
                        <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.phone}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {/* Allow typing phone number directly */}
                  {toSearchQuery && isPhoneNumber(toSearchQuery) && (
                    <div
                      className="flex items-center gap-3 p-3 bg-white dark:bg-[#1a1a1a] rounded-lg cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-2 border-purple-300 dark:border-purple-700"
                      onClick={() => handleSelectRecipient({ phone: toSearchQuery, name: toSearchQuery })}
                    >
                      <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Send to: {toSearchQuery}</p>
                        <p className="text-xs text-purple-600">New phone number</p>
                      </div>
                    </div>
                  )}
                  {filteredExternalContacts.length === 0 && !isPhoneNumber(toSearchQuery) && toSearchQuery && (
                    <p className="text-center text-muted-foreground py-8 text-sm">No contacts found. Try typing a phone number.</p>
                  )}
                  {!toSearchQuery && filteredExternalContacts.length === 0 && (
                    <p className="text-center text-muted-foreground py-8 text-sm">Type a contact name or phone number</p>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          // Selected recipient - show message input
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className={`flex-shrink-0 p-4 rounded-xl ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}>
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-12 w-12">
                  {isTeam ? (
                    <>
                      <AvatarImage src={selectedRecipient.profile_picture} />
                      <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(selectedRecipient.role)} text-white`}>
                        {getInitials(selectedRecipient)}
                      </AvatarFallback>
                    </>
                  ) : (
                    <AvatarFallback className="bg-purple-500 text-white">
                      <User className="w-5 h-5" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <p className="font-semibold">{isTeam ? getFullName(selectedRecipient) : selectedRecipient.name || selectedRecipient.phone}</p>
                  <p className={`text-xs ${isTeam ? 'text-blue-600' : 'text-purple-600'}`}>
                    {isTeam ? selectedRecipient.role : selectedRecipient.phone}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Type your first message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                  style={{ fontSize: '16px' }}
                />
                <Button onClick={handleSendMessage} className={isTeam ? TEAM_CHAT_COLOR : EXTERNAL_TEXT_COLOR}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <p className="text-xs text-center text-muted-foreground mt-3">
              {isTeam ? 'This message will be sent via Team Chat' : 'This message will be sent as an SMS (mocked)'}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Main List View
  const currentConversations = activeTab === 'team' ? teamConversations : externalConversations;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24 space-y-4" data-testid="messages-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Messages</h1>
        {isFeatureEnabled('messages_contacts') && (
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/contacts')}
          data-testid="contacts-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Contacts
        </Button>
        )}
      </div>

      {/* Two-Prong Toggle */}
      <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
        <CardContent className="p-2">
          <div className="flex rounded-xl bg-[#f5f5f5] dark:bg-[#0d0d0d] p-1">
            {isFeatureEnabled('messages_internal') && (
              <button
                onClick={() => setActiveTab('team')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'team'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                }`}
                data-testid="tab-team"
              >
                Team Chat
              </button>
            )}
            {isFeatureEnabled('messages_external') && (
              <button
                onClick={() => setActiveTab('external')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'external'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                }`}
                data-testid="tab-external"
              >
                External Texts
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* New Conversation Button - Color matches active tab */}
      <Button 
        className={`w-full ${activeTab === 'team' ? TEAM_CHAT_COLOR : EXTERNAL_TEXT_COLOR}`}
        onClick={handleStartNewConversation}
        data-testid="new-conversation-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        New Conversation
      </Button>

      {/* Conversations List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
          <span className="text-slate-500 text-sm">Loading conversations...</span>
        </div>
      ) : currentConversations.length === 0 ? (
        <Card className={`border-0 shadow-lg ${activeTab === 'team' ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}>
          <CardContent className="p-8 text-center">
            <MessageCircle className={`w-12 h-12 mx-auto mb-3 opacity-50 ${activeTab === 'team' ? 'text-blue-500' : 'text-purple-500'}`} />
            <h3 className="font-semibold mb-1">No {activeTab === 'team' ? 'team' : 'external'} conversations yet</h3>
            <p className="text-sm text-muted-foreground">
              {activeTab === 'team' 
                ? 'Start a new conversation with a team member' 
                : 'Send a text to an external contact'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {currentConversations.map((conv) => {
            const isTeam = conv.type === 'team';
            return (
              <Card 
                key={conv.id}
                className={`border-0 shadow-md hover:shadow-lg transition-all cursor-pointer ${isTeam ? TEAM_CHAT_BG : EXTERNAL_TEXT_BG}`}
                onClick={() => handleSelectConversation(conv)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      {isTeam ? (
                        <>
                          <AvatarImage src={conv.other_user?.profile_picture} />
                          <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(conv.other_user?.role)} text-white`}>
                            {getInitials(conv.other_user)}
                          </AvatarFallback>
                        </>
                      ) : (
                        <AvatarFallback className="bg-purple-500 text-white">
                          <User className="w-5 h-5" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">
                        {isTeam ? getFullName(conv.other_user) : conv.external_contact?.name || conv.phone_number}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{conv.last_message || 'No messages yet'}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isTeam ? 'text-blue-400' : 'text-purple-400'}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
